import type { Event, Thing, WithContext } from 'schema-dts';
import { AppProgramEvent } from '@/entities/program';
import type { Question } from '@/entities/question';
import type { AppBreadcrumbs } from '@/shared/components/shared/Breadcrumbs';
import { formatLocalIso8601CalendarDate } from '@/shared/lib/utils/dates';
import { composeSchemaGraph } from '@/shared/lib/utils/page-json-ld';
import {
    type SchemaContacts,
    buildPostalAddress,
    buildSchemaPlace,
    oneLine,
    schemaIdRef,
    stripHtml,
} from '@/shared/lib/utils/schema-common';
import {
    SCHEMA_EVENT_ATTENDANCE,
    SCHEMA_EVENT_STATUS,
    getSchemaIds,
    schemaPageUrl,
} from '@/shared/lib/utils/schema-ids';

interface Day {
    id: number;
    day: string;
    publishedAt: string;
}

interface Place {
    id: number;
    name: string;
}

interface EventJsonLdOptions {
    h1?: string;
    subtitle?: string;
    events?: AppProgramEvent[];
    days?: Day[];
    places?: Place[];
    pagePath?: string;
    breadcrumbs?: AppBreadcrumbs | null;
    faqQuestions?: Question[] | null;
    contacts?: SchemaContacts | null;
}

export interface ProgramDayJsonLdInput {
    title: string;
    description: string;
    publishedAt: string;
    endedAt?: string;
}

export interface NewProgramPageJsonLdOptions {
    h1: string;
    subtitle: string;
    directions: ProgramDayJsonLdInput[];
    pagePath?: string;
    breadcrumbs?: AppBreadcrumbs | null;
    faqQuestions?: Question[] | null;
    contacts?: SchemaContacts | null;
}

function sessionLocation(placeName: string | undefined, contacts?: SchemaContacts | null) {
    if (placeName?.trim()) {
        return {
            '@type': 'Place' as const,
            name: placeName.trim(),
            address: buildPostalAddress(contacts),
        };
    }
    return buildSchemaPlace(contacts);
}

export function generateNewProgramPageJsonLd({
    h1,
    subtitle,
    directions,
    pagePath = '/program/',
    breadcrumbs,
    faqQuestions,
    contacts,
}: NewProgramPageJsonLdOptions): WithContext<Thing>[] {
    const ids = getSchemaIds();
    const pageUrl = schemaPageUrl(pagePath);
    const description = oneLine(stripHtml(subtitle || ''));

    const dayEvents: Event[] = (directions || [])
        .map((card) => {
            const start = card.publishedAt ? formatLocalIso8601CalendarDate(new Date(card.publishedAt)) : null;
            if (!start) return null;
            const endRaw = card.endedAt?.trim() ? formatLocalIso8601CalendarDate(new Date(card.endedAt)) : start;
            const desc = oneLine(stripHtml(card.description || ''));
            const ev: Event = {
                '@type': 'Event',
                name: card.title,
                ...(desc ? { description: desc } : {}),
                startDate: start,
                endDate: endRaw ?? start,
                eventStatus: SCHEMA_EVENT_STATUS,
                eventAttendanceMode: SCHEMA_EVENT_ATTENDANCE,
                location: buildSchemaPlace(contacts),
                organizer: schemaIdRef(ids.organization) as Event['organizer'],
                superEvent: schemaIdRef(ids.forum) as Event['superEvent'],
            };
            return ev;
        })
        .filter(Boolean) as Event[];

    const event: WithContext<Event> = {
        '@context': 'https://schema.org',
        '@type': 'Event',
        '@id': `${pageUrl}#event`,
        name: oneLine(h1),
        url: pageUrl,
        ...(description ? { description } : {}),
        inLanguage: 'ru',
        eventStatus: SCHEMA_EVENT_STATUS,
        eventAttendanceMode: SCHEMA_EVENT_ATTENDANCE,
        location: buildSchemaPlace(contacts),
        organizer: schemaIdRef(ids.organization) as Event['organizer'],
        superEvent: schemaIdRef(ids.forum) as Event['superEvent'],
        ...(dayEvents.length > 0 ? { subEvent: dayEvents } : {}),
    };

    return composeSchemaGraph({
        nodes: [event as WithContext<Thing>],
        breadcrumbs,
        faqQuestions,
        faqPageUrl: pageUrl,
    });
}

export function generateProgramEventJsonLd({
    h1,
    subtitle,
    events,
    days,
    places,
    pagePath = '/program-2025/',
    breadcrumbs,
    faqQuestions,
    contacts,
}: EventJsonLdOptions): WithContext<Thing>[] {
    const ids = getSchemaIds();
    const pageUrl = schemaPageUrl(pagePath);
    const description = oneLine(stripHtml(subtitle || ''));

    const subEvents: Event[] = (events || [])
        .map((event) => {
            const matchingDay = (days || []).find((day) => day.id === event.day);
            if (!matchingDay?.publishedAt) return null;
            const matchingPlace = (places || []).find((place) => place.id === event.place);
            const timeMatch = event.time.match(/(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})/);
            const dayIso = formatLocalIso8601CalendarDate(new Date(matchingDay.publishedAt));
            if (!dayIso) return null;
            const toLocalIsoDateTime = (timeHhMm: string) => {
                const parts = timeHhMm.split(':');
                if (parts.length < 2) return undefined;
                return `${dayIso}T${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}:00`;
            };
            const startDateTime = timeMatch ? toLocalIsoDateTime(timeMatch[1]) : dayIso;
            const endDateTime = timeMatch ? toLocalIsoDateTime(timeMatch[2]) : dayIso;
            const desc = event.description ? oneLine(stripHtml(event.description)) : '';

            const subEvent: Event = {
                '@type': 'Event',
                name: event.name,
                ...(desc ? { description: desc } : {}),
                startDate: startDateTime,
                endDate: endDateTime,
                eventStatus: SCHEMA_EVENT_STATUS,
                eventAttendanceMode: SCHEMA_EVENT_ATTENDANCE,
                location: sessionLocation(matchingPlace?.name, contacts),
                organizer: schemaIdRef(ids.organization) as Event['organizer'],
            };
            return subEvent;
        })
        .filter(Boolean) as Event[];

    const dayDates = (days || [])
        .map((day) => new Date(day.publishedAt))
        .filter((date) => !Number.isNaN(date.getTime()))
        .sort((a, b) => a.getTime() - b.getTime());
    const startDateIso = dayDates.length ? formatLocalIso8601CalendarDate(dayDates[0]) : undefined;
    const endDateIso = dayDates.length ? formatLocalIso8601CalendarDate(dayDates[dayDates.length - 1]) : undefined;

    const eventData: WithContext<Event> = {
        '@context': 'https://schema.org',
        '@type': 'Event',
        '@id': `${pageUrl}#event`,
        name: oneLine(h1 || ''),
        url: pageUrl,
        ...(description ? { description } : {}),
        ...(startDateIso ? { startDate: startDateIso } : {}),
        ...(endDateIso ? { endDate: endDateIso } : {}),
        inLanguage: 'ru',
        eventStatus: SCHEMA_EVENT_STATUS,
        eventAttendanceMode: SCHEMA_EVENT_ATTENDANCE,
        location: buildSchemaPlace(contacts),
        organizer: schemaIdRef(ids.organization) as Event['organizer'],
        superEvent: schemaIdRef(ids.forum) as Event['superEvent'],
        ...(subEvents.length > 0 ? { subEvent: subEvents } : {}),
    };

    return composeSchemaGraph({
        nodes: [eventData as WithContext<Thing>],
        breadcrumbs,
        faqQuestions,
        faqPageUrl: pageUrl,
    });
}
